import { useCallback, useMemo } from 'react';
import { useEditor } from '@craftjs/core';
import { useContentComposer } from '../content-composer-provider';
import { ComposerSectionProps } from '../types';
import { isComposerSection } from '../utils/is-composer-section';
import { useComposerSection } from './use-composer-section';

/**
 * Provides functionality to show and hide an overlay that appears over the settings panel
 * when users interact with composer sections. The overlay state is managed through the
 * composer section's metadata and persists until the underlying Craft node is removed.
 */
export const useSettingsOverlay = () => {
  const { parts } = useContentComposer();
  const {
    id,
    meta,
    props: { sectionType },
    setMeta,
  } = useComposerSection<ComposerSectionProps>();

  const { query } = useEditor();

  const setMetaSafely = useCallback(
    (cb: Parameters<typeof setMeta>[0]) => {
      if (!query.node(id).get()) return;
      setMeta(cb);
    },
    [id, query, setMeta]
  );

  const isOverlayVisible = useMemo(() => !!meta?.showSettingsOverlay, [meta?.showSettingsOverlay]);

  const setIsOverlayVisible = useCallback(
    (visible: boolean) =>
      setMetaSafely((data) => {
        data.showSettingsOverlay = visible;
      }),
    [setMetaSafely]
  );

  const hideOverlay = useCallback(() => setIsOverlayVisible(false), [setIsOverlayVisible]);

  const showOverlay = useCallback(() => setIsOverlayVisible(true), [setIsOverlayVisible]);

  const resetOverlayVisibility = useCallback(() => {
    const Part = parts[sectionType];
    if (isComposerSection(Part)) {
      const defaultSetting = Part.craft.custom?.showSettingsOverlay;
      setMetaSafely((data) => {
        data.showSettingsOverlay = defaultSetting;
      });
    }
  }, [parts, sectionType, setMetaSafely]);

  return {
    /** Hides the settings overlay by setting meta.showSettingsOverlay to false */
    hideOverlay,

    /** Current visibility of the settings overlay */
    isOverlayVisible,

    /** Resets overlay visibility to the composer section's default state (from craft.custom.showSettingsOverlay) */
    resetOverlayVisibility,

    /** Shows the settings overlay by setting meta.showSettingsOverlay to true */
    showOverlay,
  };
};
